/**
 * G2eGente - Verificação de Vínculos de Cargos
 * Mostra quais cargos de empregados estão ou não vinculados às descrições de cargo
 */
import React, { useState, useEffect } from 'react';
import { employeesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { 
  Loader2, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  FileText,
  Users,
  Link2,
  Link2Off
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

export default function CargosVerificacao() {
  const { selectedCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedCompany?.company_id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await employeesAPI.verificarCargos(selectedCompany?.company_id);
      setData(res.data);
    } catch (error) {
      toast.error('Erro ao carregar verificação de cargos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-slate-500">
        Erro ao carregar dados
      </div>
    );
  }

  const { resumo, sem_codigo_cargo, cargos_texto_sem_codigo, codigo_sem_descricao, vinculados_corretamente, descricoes_sem_empregados } = data;

  return (
    <div className="space-y-6">
      {/* Header com botão de refresh */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Verificacao de Vinculos de Cargos</h2>
          <p className="text-sm text-slate-500">Compare cargos dos empregados com as descricoes de cargo cadastradas</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-slate-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-slate-500" />
              <div>
                <p className="text-2xl font-bold">{resumo.total_empregados}</p>
                <p className="text-xs text-slate-500">Total de Empregados</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Link2 className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{resumo.vinculados_corretamente}</p>
                <p className="text-xs text-green-600">Vinculados Corretamente</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">{resumo.sem_codigo_cargo}</p>
                <p className="text-xs text-amber-600">Sem Codigo de Cargo</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Link2Off className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-700">{resumo.codigo_sem_descricao}</p>
                <p className="text-xs text-red-600">Codigo sem Descricao</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accordion com detalhes */}
      <Accordion type="multiple" className="space-y-4">
        {/* Empregados sem código de cargo */}
        {sem_codigo_cargo.length > 0 && (
          <AccordionItem value="sem-codigo" className="border rounded-lg">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="font-medium">Empregados sem Codigo de Cargo</span>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  {sem_codigo_cargo.length}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <p className="text-sm text-slate-500 mb-4">
                Estes empregados nao possuem codigo de cargo preenchido. Edite o cadastro para adicionar o codigo.
              </p>
              
              {/* Agrupado por cargo */}
              {Object.entries(cargos_texto_sem_codigo || {}).map(([cargo, emps]) => (
                <div key={cargo} className="mb-4">
                  <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {cargo} 
                    <Badge variant="secondary" className="text-xs">{emps.length}</Badge>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 ml-6">
                    {emps.map((emp) => (
                      <div key={emp.id} className="text-sm p-2 bg-slate-50 rounded">
                        {emp.nome}
                        {emp.departamento && (
                          <span className="text-slate-400 text-xs ml-2">({emp.departamento})</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Códigos sem descrição correspondente */}
        {codigo_sem_descricao.length > 0 && (
          <AccordionItem value="sem-descricao" className="border rounded-lg border-red-200">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-600" />
                <span className="font-medium">Codigos de Cargo sem Descricao Cadastrada</span>
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                  {codigo_sem_descricao.length}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <p className="text-sm text-slate-500 mb-4">
                Estes empregados possuem codigo de cargo que nao tem descricao de cargo correspondente cadastrada.
                Cadastre a descricao de cargo ou corrija o codigo.
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead>Codigo</TableHead>
                    <TableHead>Departamento</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codigo_sem_descricao.map((emp) => (
                    <TableRow key={emp.id}>
                      <TableCell className="font-medium">{emp.nome}</TableCell>
                      <TableCell>{emp.cargo}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-red-50 text-red-700">
                          {emp.codigo_cargo}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">{emp.departamento || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Vinculados corretamente */}
        {vinculados_corretamente.length > 0 && (
          <AccordionItem value="vinculados" className="border rounded-lg border-green-200">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium">Vinculados Corretamente</span>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  {vinculados_corretamente.length}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead>Codigo</TableHead>
                    <TableHead>Descricao de Cargo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vinculados_corretamente.map((emp) => (
                    <TableRow key={emp.id}>
                      <TableCell className="font-medium">{emp.nome}</TableCell>
                      <TableCell>{emp.cargo}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          {emp.codigo_cargo}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-green-700">{emp.descricao_cargo}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Descrições sem empregados */}
        {descricoes_sem_empregados.length > 0 && (
          <AccordionItem value="descricoes-sem-emp" className="border rounded-lg">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-500" />
                <span className="font-medium">Descricoes de Cargo sem Empregados</span>
                <Badge variant="outline">
                  {descricoes_sem_empregados.length}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <p className="text-sm text-slate-500 mb-4">
                Estas descricoes de cargo nao possuem nenhum empregado vinculado.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {descricoes_sem_empregados.map((jp) => (
                  <div key={jp.id} className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-medium">{jp.titulo}</p>
                    <Badge variant="secondary" className="text-xs mt-1">
                      {jp.codigo_cargo}
                    </Badge>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      {/* Mensagem quando tudo está ok */}
      {resumo.sem_codigo_cargo === 0 && resumo.codigo_sem_descricao === 0 && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-800">Todos os cargos estao vinculados!</h3>
            <p className="text-green-600">Nenhuma inconsistencia encontrada entre empregados e descricoes de cargo.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
